/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSpendRuleActorAttributesRequest,
  ListSpendRuleActorAttributesSecurity,
} from "../models/operations/listspendruleactorattributes.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSpendRulesActorAttributesQuery,
  prefetchSpendRulesActorAttributes,
  queryKeySpendRulesActorAttributes,
  SpendRulesActorAttributesQueryData,
} from "./spendRulesActorAttributes.core.js";
export {
  buildSpendRulesActorAttributesQuery,
  prefetchSpendRulesActorAttributes,
  queryKeySpendRulesActorAttributes,
  type SpendRulesActorAttributesQueryData,
};

export type SpendRulesActorAttributesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listActorAttributes spendRules
 *
 * @remarks
 * List the member attributes a rule target condition can be written against, with each attribute's value kind. Static reference data that powers the rule editor's attribute picker.
 */
export function useSpendRulesActorAttributes(
  request?: ListSpendRuleActorAttributesRequest | undefined,
  security?: ListSpendRuleActorAttributesSecurity | undefined,
  options?: QueryHookOptions<
    SpendRulesActorAttributesQueryData,
    SpendRulesActorAttributesQueryError
  >,
): UseQueryResult<
  SpendRulesActorAttributesQueryData,
  SpendRulesActorAttributesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSpendRulesActorAttributesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listActorAttributes spendRules
 *
 * @remarks
 * List the member attributes a rule target condition can be written against, with each attribute's value kind. Static reference data that powers the rule editor's attribute picker.
 */
export function useSpendRulesActorAttributesSuspense(
  request?: ListSpendRuleActorAttributesRequest | undefined,
  security?: ListSpendRuleActorAttributesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SpendRulesActorAttributesQueryData,
    SpendRulesActorAttributesQueryError
  >,
): UseSuspenseQueryResult<
  SpendRulesActorAttributesQueryData,
  SpendRulesActorAttributesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSpendRulesActorAttributesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSpendRulesActorAttributesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SpendRulesActorAttributesQueryData,
): SpendRulesActorAttributesQueryData | undefined {
  const key = queryKeySpendRulesActorAttributes(...queryKeyBase);

  return client.setQueryData<SpendRulesActorAttributesQueryData>(key, data);
}

export function invalidateSpendRulesActorAttributes(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "actorAttributes", "list", ...queryKeyBase],
  });
}

export function invalidateAllSpendRulesActorAttributes(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "actorAttributes", "list"],
  });
}
